import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SubpageShell from '../pages/SubpageShell';
import SEO from '../components/SEO';
import { useMember, formatApiError } from './MemberAuthContext';
import '../styles/Access.css';
import '../styles/AccessAuth.css';

export const ForgotPassword = () => {
  const { forgot } = useMember();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await forgot(email.trim().toLowerCase());
      setDone(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, 'Could not send the reset link. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SubpageShell currentPath="/access/forgot-password">
      <SEO title="Forgot password — N3URO Access" description="Reset your N3URO Access password." path="/access/forgot-password" noindex />
      <section className="access-auth-band">
        <div className="access-auth-card" data-testid="forgot-card">
          <h1 className="access-auth-title">Reset your password</h1>
          {!done ? (
            <>
              <p className="access-auth-lede">Enter your account email and we&rsquo;ll send a reset link.</p>
              <form onSubmit={submit} className="access-signup-form">
                <input className="access-input" type="email" placeholder="Email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" autoComplete="email" />
                {error && <p className="access-auth-error">{error}</p>}
                <button className="access-signup-cta" disabled={busy} data-testid="forgot-submit">
                  {busy ? 'SENDING…' : 'SEND RESET LINK'}
                </button>
              </form>
            </>
          ) : (
            <p className="access-auth-lede" data-testid="forgot-done">
              If that email is registered we&rsquo;ve sent a reset link. Check
              your inbox. The link is valid for one hour.
            </p>
          )}
          <div className="access-auth-links"><Link to="/access/sign-in">Back to sign in</Link></div>
        </div>
      </section>
    </SubpageShell>
  );
};

export const ResetPassword = () => {
  const { reset } = useMember();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const token = params.get('token') || '';

  useEffect(() => { if (!token) setError('This reset link is invalid.'); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true); setError('');
    try {
      await reset(token, password);
      navigate('/access/account', { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, 'Could not reset your password.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SubpageShell currentPath="/access/reset-password">
      <SEO title="Reset password — N3URO Access" description="Choose a new password for your N3URO Access account." path="/access/reset-password" noindex />
      <section className="access-auth-band">
        <div className="access-auth-card" data-testid="reset-card">
          <h1 className="access-auth-title">Choose a new password</h1>
          <form onSubmit={submit} className="access-signup-form">
            <input className="access-input" type="password" placeholder="New password (min 8 characters)" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" autoComplete="new-password" />
            {error && <p className="access-auth-error">{error}</p>}
            <button className="access-signup-cta" disabled={busy || !token} data-testid="reset-submit">
              {busy ? 'SAVING…' : 'SET NEW PASSWORD'}
            </button>
          </form>
        </div>
      </section>
    </SubpageShell>
  );
};
